import { CircularProgress } from '@material-ui/core';
import { modeUIContext, setIsEditingForm, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { NumericMetricInfo, RobotInfo, TagMetricInfo, TagMetricEnum, SortOrder } from '@moderepo/mode-apis';
import { BaseEntityField, capitalizeAll, getRobotDefinitionByRobotClass, getRobotTimeSeriesCollectionId, GenericMessageBox,
    roundValue, usePropValue } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRobotById, fetchRobotCatalog, fetchRobotCloudProjectSettings, rcDataContext, selectRobotById, selectRobotCatalog,
    selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchTimeSeriesCollectionInfo, fetchTimeSeriesCollectionRawData, userAppDataContext, selectTimeSeriesCollectionInfo,
    selectTimeSeriesCollectionRawData } from '@moderepo/mode-data-state-user-app';
import { MetricInfo, RCMetricsValuesDisplay, RCMetricsValuesDisplayProps } from '../../components/robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCRobotPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';
import { getDateTimeDisplay } from '../containerUtils';


/**
 * The structure for storing the latest metrics info for 1 robot
 */
interface RobotLatestMetricsValues {
    readonly timestamp: string | undefined;
    readonly numericMetrics: readonly MetricInfo[];
    readonly tagMetrics: readonly MetricInfo[];
}



export interface RCRobotLatestMetricDisplayContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly userId?: number;
    readonly homeId: number;
    readonly deviceId?: number;
    readonly robotId: string;
    readonly hiddenMetrics: {
        readonly numericMetrics: {
            readonly [fieldName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [fieldName: string]: boolean;
        };
    };
    readonly onHiddenMetricsListChanged?: (hiddenMetrics: {
        readonly numericMetrics: {
            readonly [metricName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [metricName: string]: boolean;
        };
    })=> void;
    readonly onDownloadDataClicked?: (robot: RobotInfo)=> void;
}


/**
 * This component displays all the metrics for 1 robot
 * @param props
 */
export const RCRobotLatestMetricDisplayContainer: React.FC<RCRobotLatestMetricDisplayContainerProps> = (
    props: RCRobotLatestMetricDisplayContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const projectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);
    const robot = selectRobotById(rcDataState, props.homeId, props.robotId);

    const [isEditing, setIsEditing] = useState<boolean>();

    const [hiddenMetricValueNames, setHiddenMetricValueNames] = usePropValue<{
        readonly numericMetrics: {
            readonly [fieldName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [fieldName: string]: boolean;
        };
    }>(props.hiddenMetrics);

    const { trans } = useRitzTranslation(project);


    // This state is used for remembering the params we used for fetching data so that we can use the same params to select the data from data state
    const fetchDataParams = useRef<{
        readonly date: string,
        readonly valueNames: readonly string[];
        readonly tagNames: readonly string[];
    } | undefined>();


    // Wait for collection Info data to be loaded and select it from the data state
    const collectionInfo = useMemo(() => {
        if (projectSettings && robot) {
            const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);
            return selectTimeSeriesCollectionInfo(appDataState, robot.homeId, projectSettings.tsdbSmartModuleId, collectionId);
        }
        return undefined;
    }, [appDataState, projectSettings, robot]);


    const [robotLatestMetricsValues, setRobotLatestMetricsValues] = useState<RobotLatestMetricsValues | undefined>();



    // This useEffect is used for loading RC project settings
    useEffect(() => {
        if (!projectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [projectSettings, props.projectId, rcDataDispatch]);


    // This useEffect is used for loading Robot Catalog on component init
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error
            });
        }
    }, [props.projectId, rcDataDispatch, robotCatalog]);


    // This useEffect is used for fetching robot data
    useEffect(() => {
        if (!robot) {
            rcDataDispatch(fetchRobotById(props.projectId, props.homeId, props.robotId)).catch(() => {
                // ignore error
            });
        }
    }, [props.homeId, props.projectId, props.robotId, rcDataDispatch, robot]);


    // This use effect is used for fetching Collection INFO so that we know what metric data we need to load
    useEffect(() => {
        if (projectSettings && robotCatalog && robot && !collectionInfo) {
            const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);
            appDataDispatch(
                fetchTimeSeriesCollectionInfo(robot.homeId, projectSettings.tsdbSmartModuleId, collectionId), false, true,
            );
        }
    }, [appDataDispatch, collectionInfo, projectSettings, robot, robotCatalog]);


    // Wait for Collection Info is ready then find out what valueNames/tagNames the collection has and make API call to fetch
    // collection data for those metrics.
    useEffect(() => {
        if (robot && projectSettings && collectionInfo) {
            const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);
            const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);
            const today = (new Date()).toISOString();

            if (robotDef) {
                // Get the list of valueNames to fetch data for. The valueNames from the collectionInfo might contain old valueNames that might
                // no longer used therefore we need to compare them with the robotDefinition's numericMetrics' value names and filter out
                // valueNames that are no longer defined in the robotDefinition
                const valueNames = (robotDef && collectionInfo ? collectionInfo.valueNames.filter((valueName: string) => {
                    return robotDef.numericMetrics.find((numericMetric: NumericMetricInfo) => {
                        return numericMetric.name === valueName;
                    }) !== undefined;
                }) : collectionInfo?.valueNames) || [];

                // Get the list of Tags from the robot definition or use the collectionInfo.tagNames
                const tagNames = (robotDef && collectionInfo && collectionInfo.tagNames ? collectionInfo.tagNames.filter((tagName: string) => {
                    return robotDef.tagMetrics?.find((tagMetric: TagMetricInfo) => {
                        return tagMetric.name === tagName;
                    }) !== undefined;
                }) : collectionInfo?.tagNames) || [];


                // After the data is loaded, we will need to select it from the data state. To select it from the data state, we will need to specify
                // the params we use when we fetch the data. Therefore, we need to save these params so that we can use them to select data later.
                fetchDataParams.current = {
                    date: today, valueNames, tagNames,
                };

                // Now fetch collectionData for the list of valueNames and tagNames
                appDataDispatch(fetchTimeSeriesCollectionRawData(
                    robot.homeId, projectSettings.tsdbSmartModuleId, collectionId,
                    fetchDataParams.current?.date, -1, fetchDataParams.current.valueNames, fetchDataParams.current.tagNames,
                ), false);
            }
        }
    }, [appDataDispatch, collectionInfo, projectSettings, robot, robotCatalog]);



    // Wait for collection data to be loaded and then use the data to create a RobotLatestMetricsValues
    useEffect(() => {
        if (projectSettings && robot && fetchDataParams.current && !robotLatestMetricsValues) {
            const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);

            // Use the fetchDataParams we saved earlier to fetch the collection data
            const lastFetchDataParams = fetchDataParams.current;
            const collectionData = selectTimeSeriesCollectionRawData(
                appDataState, robot.homeId, projectSettings.tsdbSmartModuleId, collectionId, lastFetchDataParams.date, -1,
                lastFetchDataParams.valueNames, lastFetchDataParams.tagNames,
            );

            if (collectionData) {
                // We have fetch and select the data successfully, we can now clear the fetchDataParams
                fetchDataParams.current = undefined;

                setRobotLatestMetricsValues({
                    timestamp     : collectionData.data[0][0] as string,       // The first element in the data will be the date of the values
                    numericMetrics: lastFetchDataParams.valueNames.map((valueName: string, index: number): MetricInfo => {
                        return {
                            value: collectionData.data[0][index + 1] ?? undefined,
                            name : valueName,
                        };
                    }),
                    tagMetrics: lastFetchDataParams.tagNames.map((tagName: string, index: number): MetricInfo => {
                        return {
                            value: collectionData.data[0][lastFetchDataParams.valueNames.length + index + 1] ?? undefined,
                            name : tagName,
                        };
                    }),
                });
            }
        }
    }, [appDataState, dataState, projectSettings, robot, robotLatestMetricsValues]);


    /**
     * Create 1 field for each metric. This set of fields will be displayed in the RCMetricValuesDisplay component
     */
    const fieldSettings = useMemo((): RCMetricsValuesDisplayProps['fields'] | undefined => {
        if (robot && robotLatestMetricsValues) {
            const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);

            const numericMetricFields = robotLatestMetricsValues.numericMetrics.reduce((
                result: { [fieldName: string]: BaseEntityField<string> },
                metricInfo: MetricInfo,
            ): {
                [fieldName: string]: BaseEntityField<string>
            } => {
                // Try to find the metric definition from the robot definition
                const metricDef = robotDef?.numericMetrics.find((metric: NumericMetricInfo) => {
                    return metric.name === metricInfo.name;
                });

                return {
                    ...result,
                    [metricInfo.name]: {
                        label: metricDef?.displayName || metricInfo.name,
                        value: metricInfo.value !== undefined && metricInfo.value !== null
                            ? trans('label.value_unit_display', {
                                value: roundValue(Number(metricInfo.value), 3),
                                unit : metricDef?.unit,
                            })
                            : trans('label.none'),
                        hidden: hiddenMetricValueNames && hiddenMetricValueNames.numericMetrics
                        && hiddenMetricValueNames.numericMetrics[metricInfo.name],
                    },
                };
            }, {});

            const tagMetricFields = robotLatestMetricsValues.tagMetrics.reduce((
                result: { [fieldName: string]: BaseEntityField<string> },
                metricInfo: MetricInfo,
            ): {
                [fieldName: string]: BaseEntityField<string>
            } => {
                // Try to find the metric definition from the robot definition
                const metricDef = robotDef?.tagMetrics?.find((metric: TagMetricInfo) => {
                    return metric.name === metricInfo.name;
                });

                const valueMap = metricDef?.enumDefinitions ? metricDef.enumDefinitions.reduce((
                    valueMapResult: {
                        [value: string]: string
                    },
                    mappedValue: TagMetricEnum,
                ) => {
                    return {
                        ...valueMapResult,
                        [mappedValue.value]: mappedValue.name,
                    };
                }, {}) : {};

                if (metricDef) {
                    return {
                        ...result,
                        [metricInfo.name]: {
                            label : metricDef?.displayName || metricInfo.name,
                            value : metricInfo.value !== undefined ? valueMap[metricInfo.value] || metricInfo.value : trans('label.none'),
                            hidden: hiddenMetricValueNames && hiddenMetricValueNames && hiddenMetricValueNames.tagMetrics[metricInfo.name],
                        },
                    };
                }

                return {};
            }, {});

            return {
                numericMetricFields,
                tagMetricFields,
            };
        }

        return undefined;
    }, [hiddenMetricValueNames, robotLatestMetricsValues, robot, robotCatalog, trans]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onFormSubmit = useCallback((hiddenMetrics: {
        readonly numericMetrics: {
            readonly [metricName: string]: boolean;
        };
        readonly tagMetrics: {
            readonly [metricName: string]: boolean;
        };
    }) => {
        setHiddenMetricValueNames(hiddenMetrics);

        closeEditing();

        if (props.onHiddenMetricsListChanged) {
            props.onHiddenMetricsListChanged(hiddenMetrics);
        }
    }, [closeEditing, props, setHiddenMetricValueNames]);


    const onViewRobotDetailsClick = useCallback((selectedRobot: RobotInfo) => {
        const options: RCRobotPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ROBOT_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    homeId   : selectedRobot.homeId,
                    robotId  : selectedRobot.id,
                },
                context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.deviceId, props.homeId, props.projectId, props.userId, uiDispatch]);


    return (
        <>
            {robot && robotLatestMetricsValues && fieldSettings && (
                <RCMetricsValuesDisplay
                    className={clsx(props.className, 'match-height', isEditing && 'editing')}
                    isEditing={isEditing}
                    title={robot.name || robot.id}
                    subtitle={`${capitalizeAll(trans('label.robot_class'))}: ${robot.robotClass}`}
                    fieldsOrdering={SortOrder.ASC}
                    timestamp={robotLatestMetricsValues.timestamp
                        ? getDateTimeDisplay(robotLatestMetricsValues.timestamp, trans)
                        : trans('label.none')}
                    fields={fieldSettings}
                    actions={{
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: closeEditing,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onFormSubmit,
                        },
                    }}
                    customSecondaryActions={[{
                        label  : capitalizeAll(trans('label.edit')),
                        icon   : 'edit',
                        onClick: () => {
                            onChangeEditingMode(true);
                        },
                    }, {
                        label  : capitalizeAll(trans('label.view_details')),
                        icon   : 'info',
                        onClick: () => {
                            onViewRobotDetailsClick(robot);
                        },
                    }, {
                        label  : capitalizeAll(trans('label.download_time_series_data')),
                        icon   : 'get_app',
                        onClick: () => {
                            if (props.onDownloadDataClicked) {
                                props.onDownloadDataClicked(robot);
                            }
                        },
                    }]}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    text={{
                        updatedDate: capitalizeAll(trans('label.updated_date')),
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!(robot && robotLatestMetricsValues && fieldSettings) && (
                <CircularProgress />
            )}
        </>

    );

};
